// packages/api/src/hooks/admin/useAnnouncements.ts
//
// ESCR-EPIC-26 — Hooks do módulo Novidades/Comunicados (announcements). Consome:
// - docs/contracts/ESCR-152-announcements.md (endpoints/DTOs/error codes/query keys)
//
// Duas superfícies de API sob o mesmo domínio:
// - ADMIN   → /admin/announcements (announcements:manage)
// - CONSULTOR→ /announcements       (announcements:read)
//
// Convenção paginação: { count, rows } (AnnouncementsPage). apiClient já prefixa
// `/api/v1`; respostas vêm embrulhadas em ApiSuccess<T> (desempacota res.data.data).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type {
  Announcement,
  AnnouncementAdminListPage,
  AnnouncementDetail,
  AnnouncementFeedParams,
  AnnouncementsPage,
  ApiSuccess,
  ArchiveAnnouncementDto,
  ListAnnouncementsParams,
  UpdateAnnouncementDto,
  UpsertAnnouncementDto,
} from '@alamo/types';

import { apiClient } from '../../client';
import { announcementsKeys } from '../../query-keys';

const ADMIN_BASE = '/admin/announcements';
const CONSULTOR_BASE = '/announcements';

/* -------------------------------------------------------------------------- */
/* Admin — queries                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Lista admin paginada (todos os status). GET /admin/announcements.
 * Cada row inclui `readCount`/`totalReaders` (stats de leitura por row — a coluna
 * "LEITURAS" renderiza `{readCount}/{totalReaders}` sem chamada extra).
 */
export function useAdminAnnouncements(
  params?: ListAnnouncementsParams,
): UseQueryResult<AnnouncementAdminListPage, Error> {
  return useQuery({
    queryKey: announcementsKeys.list(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<AnnouncementAdminListPage>>(ADMIN_BASE, {
        params,
      });
      return res.data.data;
    },
  });
}

/** Detalhe admin + stats de leitura. GET /admin/announcements/:id. */
export function useAdminAnnouncement(id: string | null): UseQueryResult<AnnouncementDetail, Error> {
  return useQuery({
    queryKey: announcementsKeys.detail(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<AnnouncementDetail>>(`${ADMIN_BASE}/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

/* -------------------------------------------------------------------------- */
/* Admin — mutations                                                           */
/* -------------------------------------------------------------------------- */

export function useCreateAnnouncement(): UseMutationResult<
  Announcement,
  Error,
  UpsertAnnouncementDto
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const res = await apiClient.post<ApiSuccess<Announcement>>(ADMIN_BASE, input);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementsKeys.all }),
  });
}

export function useUpdateAnnouncement(): UseMutationResult<
  Announcement,
  Error,
  { id: string; input: UpdateAnnouncementDto }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }) => {
      const res = await apiClient.patch<ApiSuccess<Announcement>>(`${ADMIN_BASE}/${id}`, input);
      return res.data.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: announcementsKeys.all });
      void qc.invalidateQueries({ queryKey: announcementsKeys.detail(id) });
    },
  });
}

export function usePublishAnnouncement(): UseMutationResult<Announcement, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.post<ApiSuccess<Announcement>>(`${ADMIN_BASE}/${id}/publish`);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementsKeys.all }),
  });
}

export function useArchiveAnnouncement(): UseMutationResult<
  Announcement,
  Error,
  { id: string; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const body: ArchiveAnnouncementDto = reason ? { reason } : {};
      const res = await apiClient.post<ApiSuccess<Announcement>>(
        `${ADMIN_BASE}/${id}/archive`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementsKeys.all }),
  });
}

export function useDeleteAnnouncement(): UseMutationResult<{ deletedAt: string }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.delete<ApiSuccess<{ deletedAt: string }>>(`${ADMIN_BASE}/${id}`);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementsKeys.all }),
  });
}

/* -------------------------------------------------------------------------- */
/* Consultor — feed + detalhe                                                  */
/* -------------------------------------------------------------------------- */

/** Feed paginado (published+visible). GET /announcements. */
export function useAnnouncements(
  params?: AnnouncementFeedParams,
): UseQueryResult<AnnouncementsPage, Error> {
  return useQuery({
    queryKey: announcementsKeys.feed(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<AnnouncementsPage>>(CONSULTOR_BASE, { params });
      return res.data.data;
    },
  });
}

/** Detalhe do consultor. GET /announcements/:id. */
export function useAnnouncement(id: string | null): UseQueryResult<Announcement, Error> {
  return useQuery({
    queryKey: announcementsKeys.detail(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<Announcement>>(`${CONSULTOR_BASE}/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

/* -------------------------------------------------------------------------- */
/* Consultor — surfaces (unread / popup / banner)                              */
/* -------------------------------------------------------------------------- */

/** Nº de não-lidos (badge do menu). GET /announcements/unread-count. */
export function useAnnouncementsUnreadCount(): UseQueryResult<{ count: number }, Error> {
  return useQuery({
    queryKey: announcementsKeys.unreadCount(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number }>>(
        `${CONSULTOR_BASE}/unread-count`,
      );
      return res.data.data;
    },
  });
}

/**
 * Carrossel de login (R2). GET /announcements/active-popup → LISTA ORDENADA
 * (cap 5) de destaques elegíveis. Vazia quando não há comunicado elegível.
 */
export function useActiveAnnouncementPopup(): UseQueryResult<Announcement[], Error> {
  return useQuery({
    queryKey: announcementsKeys.activePopup(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<Announcement[]>>(`${CONSULTOR_BASE}/active-popup`);
      return res.data.data;
    },
  });
}

/** Banner do dashboard (R1). GET /announcements/active-banner → 1 ou null. */
export function useActiveAnnouncementBanner(): UseQueryResult<Announcement | null, Error> {
  return useQuery({
    queryKey: announcementsKeys.activeBanner(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<Announcement | null>>(
        `${CONSULTOR_BASE}/active-banner`,
      );
      return res.data.data;
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Consultor — mutations (marcar lido / dispensar banner)                      */
/* -------------------------------------------------------------------------- */

/**
 * Marca seen. PATCH /announcements/:id/read (idempotente). onSuccess invalida
 * feed + unread-count + popup + banner (o backend também emite count_changed).
 */
export function useMarkAnnouncementRead(): UseMutationResult<{ seenAt: string }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.patch<ApiSuccess<{ seenAt: string }>>(
        `${CONSULTOR_BASE}/${id}/read`,
      );
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementsKeys.all }),
  });
}

/** Dispensa o banner. PATCH /announcements/:id/dismiss-banner (idempotente). */
export function useDismissAnnouncementBanner(): UseMutationResult<
  { dismissedAt: string },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.patch<ApiSuccess<{ dismissedAt: string }>>(
        `${CONSULTOR_BASE}/${id}/dismiss-banner`,
      );
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementsKeys.activeBanner() }),
  });
}
